import React, { Component, Fragment } from 'react';
import {
  ApplicationPaths,
  LogoutActions,
  QueryParameterNames,
} from '../api-authorization/ApiAuthorizationConstants';
import authService, {
  AuthenticationResultStatus,
} from '../api-authorization/AuthorizeService';

type LogoutAction = (typeof LogoutActions)[keyof typeof LogoutActions];

interface LogoutProcessProps {
  action: LogoutAction;
}

interface LogoutProcessState {
  message?: string;
  isReady: boolean;
  authenticated: boolean;
}

// Handles the user's logout process.
export class LogoutProcess extends Component<
  LogoutProcessProps,
  LogoutProcessState
> {
  constructor(props: LogoutProcessProps) {
    super(props);
    this.state = {
      message: undefined,
      isReady: false,
      authenticated: false,
    };
  }

  async componentDidMount(): Promise<void> {
    const action = this.props.action;

    switch (action) {
      case LogoutActions.Logout: {
        // Guarded read of history.state.state.local
        const localInitiated = Boolean(
          (window.history.state as any)?.state?.local,
        );

        if (localInitiated) {
          void this.logout(this.getReturnUrl());
        } else {
          // Prevent external direct hits to /authentication/logout from triggering logout
          this.setState({
            isReady: true,
            message: 'The logout was not initiated from within the page.',
          });
        }
        break;
      }
      case LogoutActions.LogoutCallback:
        void this.processLogoutCallback();
        break;

      case LogoutActions.LoggedOut:
        this.setState({
          isReady: true,
          message: 'You successfully logged out!',
        });
        window.location.href = '/';
        break;

      default:
        throw new Error(`Invalid action '${action}'`);
    }

    void this.populateAuthenticationState();
  }

  render(): React.ReactNode {
    const { isReady, message } = this.state;
    if (!isReady) {
      return <div className="container-authorization-service" />;
    }
    if (message) {
      return <div className="container-authorization-service">{message}</div>;
    }

    const action = this.props.action;
    switch (action) {
      case LogoutActions.Logout:
        return (
          <Fragment>
            <div className="container-authorization-service">
              Processing logout
            </div>
          </Fragment>
        );
      case LogoutActions.LogoutCallback:
        return (
          <Fragment>
            <div className="container-authorization-service">
              Processing logout callback
            </div>
          </Fragment>
        );
      case LogoutActions.LoggedOut:
        return <div className="container-authorization-service">{message}</div>;
      default:
        throw new Error(`Invalid action '${action}'`);
    }
  }

  private async logout(returnUrl: string): Promise<void> {
    const state = { returnUrl };
    const isAuthenticated = await authService.isAuthenticated();

    if (isAuthenticated) {
      const result = await authService.signOut(state);
      switch (result.status) {
        case AuthenticationResultStatus.Redirect:
          break;
        case AuthenticationResultStatus.Success:
          this.navigateToReturnUrl(returnUrl);
          break;
        case AuthenticationResultStatus.Fail:
          this.setState({ message: result.message });
          break;
        default:
          throw new Error('Invalid authentication result status.');
      }
    } else {
      this.setState({ message: 'You successfully logged out!' });
      window.location.href = '/';
    }
  }

  private async processLogoutCallback(): Promise<void> {
    const url = window.location.href;
    const result = await authService.completeSignOut(url);

    switch (result.status) {
      case AuthenticationResultStatus.Redirect:
        // completeSignOut finishing should never require another redirect
        throw new Error('Should not redirect.');
      case AuthenticationResultStatus.Success:
        this.navigateToReturnUrl(
          this.getReturnUrl(result.state as { returnUrl?: string } | undefined),
        );
        break;
      case AuthenticationResultStatus.Fail:
        this.setState({ message: result.message });
        break;
      default:
        throw new Error('Invalid authentication result status.');
    }
  }

  private async populateAuthenticationState(): Promise<void> {
    const authenticated = await authService.isAuthenticated();
    this.setState({ isReady: true, authenticated });
  }

  private getReturnUrl(state?: { returnUrl?: string }): string {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get(QueryParameterNames.ReturnUrl);

    if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
      // Prevent open redirects
      throw new Error(
        'Invalid return url. The return url needs to have the same origin as the current page.',
      );
    }

    return (
      state?.returnUrl ||
      fromQuery ||
      `${window.location.origin}${ApplicationPaths.LoggedOut}`
    );
  }

  private navigateToReturnUrl(returnUrl: string): void {
    // Replace so the callback uri w/ tokens is not left in history
    window.location.replace(returnUrl);
  }
}
